import { AppImages, Chat, navigateTo, routes } from '@/config'
import { notificationPermissionStore } from '@/stores'
import { useCallback, useEffect, useRef } from 'react'
import { useChatContext } from 'stream-chat-react'
import { ChatLocalMesage, isChatMuted } from '@packages/chat'
import { showRightPanel } from '@/hooks/useLayoutPanel'
import { getIsWindowActive, useIsWindowActive } from '@/hooks'
import { getChannelDisplayData, createMuteTimer, getMentionPreference, hasMention, buildMessageNotificationData, isThreadParticipant } from './utils'
import { ChatPageParams } from '@/types'
import { Getstream } from '@/services'

const onClickNotification = async (notif: Notification) => {
  notif.close()
  const data = notif.data ?? {}
  const { channelId, channelType, receiver, messageId, parentId } = data

  if (!channelId || !channelType || receiver != Chat.Store.userState.get()?.id) return

  window.focus()

  const isThreadMessage = !!parentId
  const queryParams: ChatPageParams = { c: channelId }

  if (isThreadMessage && parentId) {
    queryParams.t = parentId
  } else {
    delete queryParams.t
  }

  showRightPanel()

  queryParams.m = messageId

  navigateTo(`/${routes.Chat.Chat}`, queryParams)
}

// Singleton pattern to store the active NotificationsListener instance
let activeListenerInstance: {
  client: any
  muteTimers: Map<string, NodeJS.Timeout>
} | null = null

export function NotificationsListener() {
  const { client } = useChatContext()
  const permissionStatus = notificationPermissionStore.useStatus()
  useIsWindowActive()

  const unsubscribes = useRef<(ReturnType<typeof client.on> | null)[]>([])
  const muteTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const hasRequestedPermission = useRef(false)
  const addedNotifications = useRef<Notification[]>([])

  useEffect(() => {
    if (permissionStatus.available && !permissionStatus.granted && !hasRequestedPermission.current) {
      hasRequestedPermission.current = true
      notificationPermissionStore.requestIfNeeded()
    }
  }, [permissionStatus.available, permissionStatus.granted])

  useEffect(() => {
    activeListenerInstance = {
      client,
      muteTimers: muteTimeouts.current,
    };
    (window as any).createMuteTimer = createMuteTimer

    return () => {
      if (activeListenerInstance?.client === client) {
        activeListenerInstance = null
      }
      delete (window as any).createMuteTimer
    }
  }, [client])

  const clearNotifications = useCallback(() => {
    const cleared: typeof addedNotifications.current = []

    addedNotifications.current.forEach(n => {
      try {
        logger.debug('clearing notification', n.data)
        n.close()
        cleared.push(n)
      } catch (e) {
        logger.error('Failed to remove notificaiton', e)
      }
    })

    addedNotifications.current = addedNotifications.current.filter(n => !cleared.includes(n))
  }, [])

  const onWindowVisibilityChanged = useCallback(() => {
    if (document.visibilityState === 'visible') {
      // The tab has become visible so clear the now-stale Notification.
      clearNotifications()
    }
  }, [clearNotifications])

  const setupMuteExpirationTimers = useCallback(async () => {
    if (!client.user?.id) return

    try {
      const mutedChannels = client.mutedChannels || []

      mutedChannels.forEach((channelMute) => {
        const { channel } = channelMute
        const expires_at = (channelMute as any).expires_at

        if (expires_at && channel?.id && channel?.type) {
          const expirationTime = new Date(expires_at).getTime()
          const now = Date.now()

          if (expirationTime > now) {
            createMuteTimer(channel.id, channel.type, expires_at, activeListenerInstance)
          }
        }
      })
    } catch (error) {
      console.error('Error setting up mute expiration timers:', error)
    }
  }, [client])

  const setupListener = useCallback(() => {
    if (!client.user?.id) {
      return
    }

    if (!permissionStatus.granted) {
      return
    }
    setupMuteExpirationTimers()

    const handleEvent = async (event: any, shouldShow: (data: { isMyMessage: boolean; isMyEvent: boolean }) => boolean) => {
      const isNotificationEnabled = notificationPermissionStore.getEnabled()

      if (!isNotificationEnabled) {
        return
      }

      const currentPathname = window.location.pathname
      const isWindowActive = getIsWindowActive()

      if (isWindowActive && currentPathname.startsWith('/chat/list')) {
        return
      }

      const currentUser = client.user
      const currentUserId = currentUser?.id
      const eventUser = event?.user

      if (!currentUserId) return

      const message = event?.message as ChatLocalMesage

      if (message?.silent) {
        return
      }

      const messageOwner = message?.user
      const isMyMessage = messageOwner?.id === currentUserId
      // If the current user has triggered the event
      const isMyEvent = eventUser?.id === currentUserId

      if (!shouldShow({ isMyMessage, isMyEvent })) {
        return
      }

      const channel = client.channel(event.channel_type, event.channel_id)
      if (!channel) return

      const channelCid = channel?.cid || event?.cid || (event?.channel_type && event?.channel_id ? `${event.channel_type}:${event.channel_id}` : '')
      const preference = getMentionPreference(channel, currentUserId, channelCid)
      const hasMentionInMessage = hasMention(event.message, event, currentUserId)
      if (!channel.initialized) {
        await channel.watch()
      }
      const channelMuted = isChatMuted(channel)

      if (preference === 'mentions' && !hasMentionInMessage) {
        return
      }

      if (channelMuted && !(preference === 'mentions' && hasMentionInMessage)) {
        return
      }

      const isThreadReply = !!message?.parent_id && !message?.show_in_channel

      if (isThreadReply) {
        const isParticipant = await isThreadParticipant(client, message, currentUserId)
        if (!isParticipant && !hasMentionInMessage) {
          return
        }
      }

      const { title, ...notificationData } = buildMessageNotificationData(event, channel, client)

      try {
        const notif = new Notification(title, notificationData)
        addedNotifications.current.push(notif)
        notif.addEventListener('click', () => onClickNotification(notif))
      } catch (error) {
        console.error('Error showing notification:', error)
      }
    }

    const handleIncomingMessage = (event: any) => {
      handleEvent(event, ({ isMyMessage }) => !isMyMessage)
    }

    const handleIncomingReaction = (event: any) => {
      handleEvent(event, ({ isMyMessage, isMyEvent }) => {
        return isMyMessage && !isMyEvent
      })
    }

    const handleReminderDue = (event) => {
      const isNotificationEnabled = notificationPermissionStore.getEnabled()

      if (!isNotificationEnabled) {
        return
      }

      const currentUserId = client.user.id
      const [channelType, channelId] = event.cid.split(':')

      const channel = client.getChannelById(channelType, channelId, {})

      const displayData = getChannelDisplayData(channel, client.user)
      const message = event.reminder.message

      const notif = new Notification(
        'You asked me to remind you about',
        {
          // @ts-ignore
          body: message?.customMessage,
          tag: `${event.type}:${message.id}`,
          data: {
            messageId: event.message_id,
            channelId,
            channelType,
            receiver: currentUserId,
          },
          badge: AppImages.NotificationIcon.src, // Displayed on mobile status bar
          icon: displayData.icon,
        },
      )

      addedNotifications.current.push(notif)

      notif.addEventListener('click', () => {
        onClickNotification(notif)
      })
    }

    unsubscribes.current = [
      client.on('message.new', handleIncomingMessage),
      client.on('notification.message_new', handleIncomingMessage),
      client.on('reaction.new', handleIncomingReaction),
      client.on('reaction.updated', handleIncomingReaction),
      client.on('notification.reminder_due', handleReminderDue),
    ]

    document.addEventListener('visibilitychange', onWindowVisibilityChanged)
  }, [permissionStatus.granted, client.user?.id, onClickNotification, setupMuteExpirationTimers])

  const teardownListener = useCallback(() => {
    if (!!unsubscribes?.current?.length) {
      unsubscribes.current.forEach(unsub => {
        if (unsub?.unsubscribe) {
          unsub?.unsubscribe?.()
        }
      })
    }
    muteTimeouts.current.forEach(timeout => clearTimeout(timeout))
    muteTimeouts.current.clear()

    document.removeEventListener('visibilitychange', onWindowVisibilityChanged)
  }, [onWindowVisibilityChanged])

  useEffect(() => {
    setupListener()

    return () => {
      teardownListener()
    }
  }, [setupListener, teardownListener])

  return null
}

